'use client';

import { c as _c } from "react/compiler-runtime";
import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { applyCnpjMask } from '@/shared/utils/cnpj';
import { normalizeCpfCnpj } from '@/shared/utils/documentValidation';
import { reportError } from '@/shared/utils/reportError';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { OBJETIVO_LABELS, WIZARD_STEP_LABELS, WIZARD_STEP_ORDER } from '../constants';
import type { AbortableThunk, ObjetivoSelf, PerfilSelf, SelfStep, WizardStepView } from '../types';
import { adicionarMensagemUsuario, buscarContextoEmpresaSelf, carregarCamposObjetivoSelf, carregarObjetivosSelf, carregarPlanosSelf, consultarCnpjNegociacaoSelf, consultarPagamentoSelf, criarAgenteSelf, dispensarRestauracaoSessao, editarDadosPerfil, enviarMensagemSimulacaoSelf, iniciarPagamentoSelf, marcarTentouBuscar, reiniciar, restaurarSessao, selectSelfBuscandoContexto, selectSelfBuscouContexto, selectSelfCamposError, selectSelfCamposObjetivo, selectSelfCamposObjetivoDe, selectSelfCarregandoCampos, selectSelfCarregandoObjetivos, selectSelfCarregandoPlanos, selectSelfChatInput, selectSelfChatMensagens, selectSelfCnpjConsultado, selectSelfCnpjEncontrado, selectSelfCnpjError, selectSelfConsultandoCnpj, selectSelfContextoEncontrado, selectSelfContextoError, selectSelfCriandoAgente, selectSelfCriarAgenteError, selectSelfIniciandoPagamento, selectSelfInvoiceUrl, selectSelfLeadId, selectSelfPagamentoError, selectSelfLexiDigitando, selectSelfObjetivo, selectSelfObjetivosCatalogo, selectSelfObjetivosError, selectSelfPerfil, selectSelfPlanos, selectSelfPlanosCarregados, selectSelfPlanosError, selectSelfPollingPagamentoExpirado, selectSelfRestauracaoSessaoConcluida, selectSelfStep, selectSelfTentouBuscar, selectSelfTermosAceitos, selectSelfValoresParametros, selectSelfVariationId, setChatInput, setObjetivo, setPerfilCampo, setPlanoSelecionado, setStep, setTermosAceitos, setValorParametro } from '../store';
import { urlPagamentoSegura } from '@/shared/utils/pagamentoUrl';
import { objetivoPulaParametros, podeAvancarParametros } from '../utils/parametrosCampos';
import { resumoDoPlano, toPlanoNegociacaoCard } from '../utils/planos';
import { lerSessaoSelf, limparSessaoSelf, limparSessaoSelfDaAba } from '../utils/sessaoSelf';
import { PARAM_ETAPA, resolverEtapa } from './useSelfStepUrl';
import { cnpjPerfilValido, emailPerfilValido, perfilProntoParaBusca } from '../utils/perfilValidacao';
const STEPS_QUE_EXIGEM_CONFIGURACAO: SelfStep[] = ['parametros', 'revisao', 'pagamento', 'waitingPayment', 'errorPayment'];
const STEPS_RESTAURAVEIS_POR_SESSAO: SelfStep[] = ['pagamento', 'waitingPayment', 'errorPayment'];
const STEPS_COM_PLANO: SelfStep[] = ['pagamento', 'waitingPayment', 'errorPayment', 'success'];
export const useNegociacaoSelf = () => {
  const $ = _c(202);
  const dispatch = useAppDispatch();
  const searchParams = useSearchParams();
  const step = useAppSelector(selectSelfStep);
  const objetivo = useAppSelector(selectSelfObjetivo);
  const perfil = useAppSelector(selectSelfPerfil);
  const leadId = useAppSelector(selectSelfLeadId);
  const objetivosCatalogo = useAppSelector(selectSelfObjetivosCatalogo);
  const carregandoObjetivos = useAppSelector(selectSelfCarregandoObjetivos);
  const objetivosError = useAppSelector(selectSelfObjetivosError);
  const buscandoContexto = useAppSelector(selectSelfBuscandoContexto);
  const buscouContexto = useAppSelector(selectSelfBuscouContexto);
  const contextoEncontrado = useAppSelector(selectSelfContextoEncontrado);
  const tentouBuscar = useAppSelector(selectSelfTentouBuscar);
  const contextoError = useAppSelector(selectSelfContextoError);
  const consultandoCnpj = useAppSelector(selectSelfConsultandoCnpj);
  const cnpjConsultado = useAppSelector(selectSelfCnpjConsultado);
  const cnpjEncontrado = useAppSelector(selectSelfCnpjEncontrado);
  const cnpjError = useAppSelector(selectSelfCnpjError);
  const camposObjetivo = useAppSelector(selectSelfCamposObjetivo);
  const camposObjetivoDe = useAppSelector(selectSelfCamposObjetivoDe);
  const carregandoCampos = useAppSelector(selectSelfCarregandoCampos);
  const camposError = useAppSelector(selectSelfCamposError);
  const valoresParametros = useAppSelector(selectSelfValoresParametros);
  const criandoAgente = useAppSelector(selectSelfCriandoAgente);
  const criarAgenteError = useAppSelector(selectSelfCriarAgenteError);
  const planos = useAppSelector(selectSelfPlanos);
  const planosCarregados = useAppSelector(selectSelfPlanosCarregados);
  const carregandoPlanos = useAppSelector(selectSelfCarregandoPlanos);
  const planosError = useAppSelector(selectSelfPlanosError);
  const variationId = useAppSelector(selectSelfVariationId);
  const termosAceitos = useAppSelector(selectSelfTermosAceitos);
  const chatMensagens = useAppSelector(selectSelfChatMensagens);
  const chatInput = useAppSelector(selectSelfChatInput);
  const lexiDigitando = useAppSelector(selectSelfLexiDigitando);
  const invoiceUrl = useAppSelector(selectSelfInvoiceUrl);
  const iniciandoPagamento = useAppSelector(selectSelfIniciandoPagamento);
  const pagamentoError = useAppSelector(selectSelfPagamentoError);
  const pollingPagamentoExpirado = useAppSelector(selectSelfPollingPagamentoExpirado);
  const restauracaoSessaoConcluida = useAppSelector(selectSelfRestauracaoSessaoConcluida);
  const criarAgentePromiseRef = useRef(null);
  const consultaCnpjPromiseRef = useRef(null);
  let t0;
  if ($[0] !== perfil.email) {
    t0 = emailPerfilValido(perfil.email);
    $[0] = perfil.email;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const emailValido = t0;
  let t1;
  if ($[2] !== perfil.nome || $[3] !== tentouBuscar) {
    t1 = tentouBuscar && perfil.nome.trim().length === 0;
    $[2] = perfil.nome;
    $[3] = tentouBuscar;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const showNomeError = t1;
  const showEmailError = tentouBuscar && !emailValido;
  let t2;
  if ($[5] !== perfil.cnpj || $[6] !== tentouBuscar) {
    t2 = tentouBuscar && !cnpjPerfilValido(perfil.cnpj);
    $[5] = perfil.cnpj;
    $[6] = tentouBuscar;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const showCnpjError = t2;
  let t3;
  if ($[8] !== perfil.razaoSocial || $[9] !== tentouBuscar) {
    t3 = tentouBuscar && perfil.razaoSocial.trim().length === 0;
    $[8] = perfil.razaoSocial;
    $[9] = tentouBuscar;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const showRazaoSocialError = t3;
  let t4;
  if ($[11] !== perfil.nomeFantasia || $[12] !== tentouBuscar) {
    t4 = tentouBuscar && perfil.nomeFantasia.trim().length === 0;
    $[11] = perfil.nomeFantasia;
    $[12] = tentouBuscar;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const showNomeFantasiaError = t4;
  let t5;
  if ($[14] !== perfil.site || $[15] !== tentouBuscar) {
    t5 = tentouBuscar && perfil.site.trim().length === 0;
    $[14] = perfil.site;
    $[15] = tentouBuscar;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const showSiteError = t5;
  let t6;
  if ($[17] !== perfil) {
    t6 = perfilProntoParaBusca(perfil);
    $[17] = perfil;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const podeBuscar = t6;
  let t7;
  if ($[19] !== buscouContexto || $[20] !== perfil.sobre) {
    t7 = buscouContexto && perfil.sobre.trim().length > 0;
    $[19] = buscouContexto;
    $[20] = perfil.sobre;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const podeAvancarPerfil = t7;
  let t8;
  if ($[22] !== camposObjetivo || $[23] !== valoresParametros) {
    t8 = podeAvancarParametros(camposObjetivo, valoresParametros);
    $[22] = camposObjetivo;
    $[23] = valoresParametros;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const podeAvancarParametrosStep = t8;
  const currentIndex = WIZARD_STEP_ORDER[step] ?? 0;
  let t9;
  if ($[25] !== currentIndex) {
    t9 = WIZARD_STEP_LABELS.map((label, index) => ({
      label,
      filled: index <= currentIndex,
      active: index === currentIndex
    }));
    $[25] = currentIndex;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  const wizardSteps = t9;
  const objetivoLabel = objetivo ? OBJETIVO_LABELS[objetivo] : "\u2014";
  let t10;
  if ($[27] !== objetivo) {
    t10 = objetivoPulaParametros(objetivo);
    $[27] = objetivo;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  const objetivoSemParametros = t10;
  const stepAnteriorRevisao = objetivoSemParametros ? "perfil" : "parametros";
  let t11;
  if ($[29] !== planos) {
    t11 = planos.map(toPlanoNegociacaoCard);
    $[29] = planos;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const planosCards = t11;
  let t12;
  if ($[31] !== planos || $[32] !== variationId) {
    t12 = resumoDoPlano(planos.find(plano => plano.variationId === variationId) ?? null);
    $[31] = planos;
    $[32] = variationId;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  const planoResumoLabel = t12;
  let t13;
  let t14;
  if ($[34] !== carregandoPlanos || $[35] !== dispatch || $[36] !== planosCarregados || $[37] !== planosError || $[38] !== step) {
    t13 = () => {
      if (!STEPS_COM_PLANO.includes(step)) {
        return;
      }
      if (planosCarregados || carregandoPlanos || planosError) {
        return;
      }
      dispatch(carregarPlanosSelf());
    };
    t14 = [step, planosCarregados, carregandoPlanos, planosError, dispatch];
    $[34] = carregandoPlanos;
    $[35] = dispatch;
    $[36] = planosCarregados;
    $[37] = planosError;
    $[38] = step;
    $[39] = t13;
    $[40] = t14;
  } else {
    t13 = $[39];
    t14 = $[40];
  }
  useEffect(t13, t14);
  let t15;
  let t16;
  if ($[41] !== carregandoObjetivos || $[42] !== dispatch || $[43] !== objetivosCatalogo.length || $[44] !== objetivosError || $[45] !== step) {
    t15 = () => {
      if (step !== "objetivo") {
        return;
      }
      if (objetivosCatalogo.length > 0 || carregandoObjetivos || objetivosError) {
        return;
      }
      dispatch(carregarObjetivosSelf());
    };
    t16 = [step, objetivosCatalogo.length, carregandoObjetivos, objetivosError, dispatch];
    $[41] = carregandoObjetivos;
    $[42] = dispatch;
    $[43] = objetivosCatalogo.length;
    $[44] = objetivosError;
    $[45] = step;
    $[46] = t15;
    $[47] = t16;
  } else {
    t15 = $[46];
    t16 = $[47];
  }
  useEffect(t15, t16);
  let t17;
  let t18;
  if ($[48] !== camposError || $[49] !== camposObjetivoDe || $[50] !== carregandoCampos || $[51] !== dispatch || $[52] !== objetivo || $[53] !== step) {
    t17 = () => {
      if (step !== "parametros" || !objetivo) {
        return;
      }
      if (camposObjetivoDe === objetivo || carregandoCampos || camposError) {
        return;
      }
      dispatch(carregarCamposObjetivoSelf(objetivo));
    };
    t18 = [step, objetivo, camposObjetivoDe, carregandoCampos, camposError, dispatch];
    $[48] = camposError;
    $[49] = camposObjetivoDe;
    $[50] = carregandoCampos;
    $[51] = dispatch;
    $[52] = objetivo;
    $[53] = step;
    $[54] = t17;
    $[55] = t18;
  } else {
    t17 = $[54];
    t18 = $[55];
  }
  useEffect(t17, t18);
  const restauracaoTentadaRef = useRef(false);
  let t19;
  let t20;
  if ($[56] !== dispatch || $[57] !== leadId || $[58] !== searchParams) {
    t19 = () => {
      if (restauracaoTentadaRef.current) {
        return;
      }
      restauracaoTentadaRef.current = true;
      const etapaAlvo = resolverEtapa(searchParams.get(PARAM_ETAPA));
      if (!leadId && STEPS_RESTAURAVEIS_POR_SESSAO.includes(etapaAlvo)) {
        const sessao = lerSessaoSelf();
        if (sessao) {
          dispatch(restaurarSessao(sessao));
          return;
        }
      }
      dispatch(dispensarRestauracaoSessao());
    };
    t20 = [leadId, dispatch, searchParams];
    $[56] = dispatch;
    $[57] = leadId;
    $[58] = searchParams;
    $[59] = t19;
    $[60] = t20;
  } else {
    t19 = $[59];
    t20 = $[60];
  }
  useEffect(t19, t20);
  let t21;
  let t22;
  if ($[61] !== step) {
    t21 = () => {
      if (step !== "success") {
        return;
      }
      limparSessaoSelf();
    };
    t22 = [step];
    $[61] = step;
    $[62] = t21;
    $[63] = t22;
  } else {
    t21 = $[62];
    t22 = $[63];
  }
  useEffect(t21, t22);
  let t23;
  let t24;
  if ($[64] !== buscouContexto || $[65] !== dispatch || $[66] !== leadId || $[67] !== objetivo || $[68] !== restauracaoSessaoConcluida || $[69] !== step) {
    t23 = () => {
      if (!STEPS_QUE_EXIGEM_CONFIGURACAO.includes(step)) {
        return;
      }
      if (!restauracaoSessaoConcluida) {
        return;
      }
      const configuracaoMinima = Boolean(objetivo && leadId);
      const etapaRestauravel = STEPS_RESTAURAVEIS_POR_SESSAO.includes(step);
      if (configuracaoMinima && (etapaRestauravel || buscouContexto)) {
        return;
      }
      dispatch(setStep("landing"));
    };
    t24 = [step, objetivo, leadId, buscouContexto, restauracaoSessaoConcluida, dispatch];
    $[64] = buscouContexto;
    $[65] = dispatch;
    $[66] = leadId;
    $[67] = objetivo;
    $[68] = restauracaoSessaoConcluida;
    $[69] = step;
    $[70] = t23;
    $[71] = t24;
  } else {
    t23 = $[70];
    t24 = $[71];
  }
  useEffect(t23, t24);
  let t25;
  if ($[72] !== dispatch) {
    t25 = destino => dispatch(setStep(destino));
    $[72] = dispatch;
    $[73] = t25;
  } else {
    t25 = $[73];
  }
  const goTo = t25;
  let t26;
  if ($[74] !== dispatch) {
    t26 = valor => dispatch(setObjetivo(valor));
    $[74] = dispatch;
    $[75] = t26;
  } else {
    t26 = $[75];
  }
  const selecionarObjetivo = t26;
  let t27;
  if ($[76] !== dispatch) {
    t27 = (campo, valor_0) => dispatch(setPerfilCampo({
      campo,
      valor: campo === "cnpj" ? applyCnpjMask(valor_0) : valor_0
    }));
    $[76] = dispatch;
    $[77] = t27;
  } else {
    t27 = $[77];
  }
  const atualizarPerfil = t27;
  let t28;
  if ($[78] !== dispatch) {
    t28 = documento => {
      consultaCnpjPromiseRef.current?.abort();
      consultaCnpjPromiseRef.current = dispatch(consultarCnpjNegociacaoSelf(documento));
    };
    $[78] = dispatch;
    $[79] = t28;
  } else {
    t28 = $[79];
  }
  const dispararConsultaCnpj = t28;
  let t29;
  if ($[80] !== cnpjConsultado || $[81] !== dispararConsultaCnpj || $[82] !== perfil.cnpj) {
    t29 = () => {
      const documento_0 = normalizeCpfCnpj(perfil.cnpj);
      if (!cnpjPerfilValido(documento_0)) {
        return;
      }
      if (documento_0 === cnpjConsultado) {
        return;
      }
      dispararConsultaCnpj(documento_0);
    };
    $[80] = cnpjConsultado;
    $[81] = dispararConsultaCnpj;
    $[82] = perfil.cnpj;
    $[83] = t29;
  } else {
    t29 = $[83];
  }
  const consultarCnpjEmpresa = t29;
  let t30;
  if ($[84] !== dispararConsultaCnpj || $[85] !== perfil.cnpj) {
    t30 = () => {
      const documento_1 = normalizeCpfCnpj(perfil.cnpj);
      if (!cnpjPerfilValido(documento_1)) {
        return;
      }
      dispararConsultaCnpj(documento_1);
    };
    $[84] = dispararConsultaCnpj;
    $[85] = perfil.cnpj;
    $[86] = t30;
  } else {
    t30 = $[86];
  }
  const reconsultarCnpjEmpresa = t30;
  let t31;
  if ($[87] !== dispatch || $[88] !== podeBuscar) {
    t31 = () => {
      if (!podeBuscar) {
        dispatch(marcarTentouBuscar());
        return;
      }
      dispatch(buscarContextoEmpresaSelf());
    };
    $[87] = dispatch;
    $[88] = podeBuscar;
    $[89] = t31;
  } else {
    t31 = $[89];
  }
  const buscarContexto = t31;
  let t32;
  if ($[90] !== dispatch) {
    t32 = () => dispatch(editarDadosPerfil());
    $[90] = dispatch;
    $[91] = t32;
  } else {
    t32 = $[91];
  }
  const editarDados = t32;
  let t33;
  if ($[92] !== dispatch) {
    t33 = () => {
      criarAgentePromiseRef.current = dispatch(criarAgenteSelf());
    };
    $[92] = dispatch;
    $[93] = t33;
  } else {
    t33 = $[93];
  }
  const criarAgente = t33;
  let t34;
  if ($[94] !== dispatch || $[95] !== objetivoSemParametros) {
    t34 = () => {
      if (objetivoSemParametros) {
        criarAgentePromiseRef.current = dispatch(criarAgenteSelf());
        return;
      }
      dispatch(setStep("parametros"));
    };
    $[94] = dispatch;
    $[95] = objetivoSemParametros;
    $[96] = t34;
  } else {
    t34 = $[96];
  }
  const avancarDoPerfil = t34;
  let t35;
  if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = () => {
      criarAgentePromiseRef.current?.abort();
    };
    $[97] = t35;
  } else {
    t35 = $[97];
  }
  const cancelarCriacaoAgente = t35;
  let t36;
  if ($[98] !== dispatch) {
    t36 = (key, valor_1) => dispatch(setValorParametro({
      key,
      valor: valor_1
    }));
    $[98] = dispatch;
    $[99] = t36;
  } else {
    t36 = $[99];
  }
  const definirValorParametro = t36;
  let t37;
  if ($[100] !== dispatch) {
    t37 = () => dispatch(carregarObjetivosSelf());
    $[100] = dispatch;
    $[101] = t37;
  } else {
    t37 = $[101];
  }
  const recarregarObjetivos = t37;
  let t38;
  if ($[102] !== dispatch || $[103] !== objetivo) {
    t38 = () => {
      if (objetivo) {
        dispatch(carregarCamposObjetivoSelf(objetivo));
      }
    };
    $[102] = dispatch;
    $[103] = objetivo;
    $[104] = t38;
  } else {
    t38 = $[104];
  }
  const recarregarCampos = t38;
  let t39;
  if ($[105] !== dispatch) {
    t39 = () => dispatch(buscarContextoEmpresaSelf());
    $[105] = dispatch;
    $[106] = t39;
  } else {
    t39 = $[106];
  }
  const tentarBuscaNovamente = t39;
  let t40;
  if ($[107] !== dispatch) {
    t40 = () => dispatch(carregarPlanosSelf());
    $[107] = dispatch;
    $[108] = t40;
  } else {
    t40 = $[108];
  }
  const recarregarPlanos = t40;
  let t41;
  if ($[109] !== dispatch) {
    t41 = valor_2 => dispatch(setPlanoSelecionado(valor_2));
    $[109] = dispatch;
    $[110] = t41;
  } else {
    t41 = $[110];
  }
  const definirPlano = t41;
  let t42;
  if ($[111] !== dispatch || $[112] !== termosAceitos) {
    t42 = () => dispatch(setTermosAceitos(!termosAceitos));
    $[111] = dispatch;
    $[112] = termosAceitos;
    $[113] = t42;
  } else {
    t42 = $[113];
  }
  const alternarTermos = t42;
  let t43;
  if ($[114] !== dispatch) {
    t43 = plano_0 => {
      dispatch(setPlanoSelecionado(plano_0));
      const checkoutTab = window.open("", "_blank");
      if (checkoutTab) {
        checkoutTab.opener = null;
        limparSessaoSelfDaAba(checkoutTab);
      }
      dispatch(iniciarPagamentoSelf(plano_0)).unwrap().then(t44 => {
        const {
          invoiceUrl: url,
          status
        } = t44;
        if (status !== "PENDENTE") {
          checkoutTab?.close();
          return;
        }
        if (!urlPagamentoSegura(url)) {
          checkoutTab?.close();
          reportError(new Error("Link de pagamento recusado pelo guard de URL"), {
            source: "negociacaoSelf.abrirCheckout",
            temUrl: Boolean(url)
          });
          return;
        }
        if (checkoutTab) {
          checkoutTab.location.href = url;
        } else {
          window.open(url, "_blank", "noopener,noreferrer");
        }
      }).catch(() => checkoutTab?.close());
    };
    $[114] = dispatch;
    $[115] = t43;
  } else {
    t43 = $[115];
  }
  const abrirCheckout = t43;
  let t44;
  if ($[116] !== abrirCheckout || $[117] !== termosAceitos) {
    t44 = plano_1 => {
      if (!termosAceitos) {
        return;
      }
      abrirCheckout(plano_1);
    };
    $[116] = abrirCheckout;
    $[117] = termosAceitos;
    $[118] = t44;
  } else {
    t44 = $[118];
  }
  const seguirPagamento = t44;
  let t45;
  if ($[119] !== abrirCheckout || $[120] !== dispatch || $[121] !== planos || $[122] !== termosAceitos || $[123] !== variationId) {
    t45 = () => {
      if (!termosAceitos || !variationId || !planos.some(plano_2 => plano_2.variationId === variationId)) {
        dispatch(setStep("pagamento"));
        return;
      }
      abrirCheckout(variationId);
    };
    $[119] = abrirCheckout;
    $[120] = dispatch;
    $[121] = planos;
    $[122] = termosAceitos;
    $[123] = variationId;
    $[124] = t45;
  } else {
    t45 = $[124];
  }
  const tentarPagamentoNovamente = t45;
  let t46;
  if ($[125] !== dispatch) {
    t46 = () => dispatch(consultarPagamentoSelf());
    $[125] = dispatch;
    $[126] = t46;
  } else {
    t46 = $[126];
  }
  const verificarPagamentoAgora = t46;
  let t47;
  if ($[127] !== dispatch) {
    t47 = valor_3 => dispatch(setChatInput(valor_3));
    $[127] = dispatch;
    $[128] = t47;
  } else {
    t47 = $[128];
  }
  const definirChatInput = t47;
  let t48;
  if ($[129] !== dispatch || $[130] !== lexiDigitando) {
    t48 = texto => {
      const limpo = texto.trim();
      if (!limpo || lexiDigitando) {
        return;
      }
      dispatch(adicionarMensagemUsuario(limpo));
      dispatch(enviarMensagemSimulacaoSelf(limpo));
    };
    $[129] = dispatch;
    $[130] = lexiDigitando;
    $[131] = t48;
  } else {
    t48 = $[131];
  }
  const enviarMensagem = t48;
  let t49;
  if ($[132] !== dispatch) {
    t49 = () => {
      limparSessaoSelf();
      dispatch(reiniciar());
    };
    $[132] = dispatch;
    $[133] = t49;
  } else {
    t49 = $[133];
  }
  const reiniciarFluxo = t49;
  let t50;
  if ($[134] !== alternarTermos || $[135] !== atualizarPerfil || $[136] !== avancarDoPerfil || $[137] !== buscandoContexto || $[138] !== buscarContexto || $[139] !== buscouContexto || $[140] !== camposError || $[141] !== camposObjetivo || $[142] !== carregandoCampos || $[143] !== carregandoObjetivos || $[144] !== carregandoPlanos || $[145] !== chatInput || $[146] !== chatMensagens || $[147] !== cnpjEncontrado || $[148] !== cnpjError || $[149] !== consultandoCnpj || $[150] !== consultarCnpjEmpresa || $[151] !== contextoEncontrado || $[152] !== contextoError || $[153] !== criandoAgente || $[154] !== criarAgente || $[155] !== criarAgenteError || $[156] !== definirChatInput || $[157] !== definirPlano || $[158] !== definirValorParametro || $[159] !== editarDados || $[160] !== emailValido || $[161] !== enviarMensagem || $[162] !== goTo || $[163] !== iniciandoPagamento || $[164] !== invoiceUrl || $[165] !== lexiDigitando || $[166] !== objetivo || $[167] !== objetivoLabel || $[168] !== objetivosCatalogo || $[169] !== objetivosError || $[170] !== pagamentoError || $[171] !== perfil || $[172] !== planoResumoLabel || $[173] !== planosCards || $[174] !== planosError || $[175] !== podeAvancarParametrosStep || $[176] !== podeAvancarPerfil || $[177] !== podeBuscar || $[178] !== pollingPagamentoExpirado || $[179] !== recarregarCampos || $[180] !== recarregarObjetivos || $[181] !== recarregarPlanos || $[182] !== reconsultarCnpjEmpresa || $[183] !== reiniciarFluxo || $[184] !== seguirPagamento || $[185] !== selecionarObjetivo || $[186] !== showCnpjError || $[187] !== showEmailError || $[188] !== showNomeError || $[189] !== showNomeFantasiaError || $[190] !== showRazaoSocialError || $[191] !== showSiteError || $[192] !== step || $[193] !== stepAnteriorRevisao || $[194] !== tentarBuscaNovamente || $[195] !== tentarPagamentoNovamente || $[196] !== termosAceitos || $[197] !== valoresParametros || $[198] !== variationId || $[199] !== verificarPagamentoAgora || $[200] !== wizardSteps) {
    t50 = {
      step,
      objetivo,
      perfil,
      objetivosCatalogo,
      carregandoObjetivos,
      objetivosError,
      buscandoContexto,
      buscouContexto,
      contextoEncontrado,
      contextoError,
      consultandoCnpj,
      cnpjEncontrado,
      cnpjError,
      camposObjetivo,
      carregandoCampos,
      camposError,
      valoresParametros,
      criandoAgente,
      criarAgenteError,
      planosCards,
      carregandoPlanos,
      planosError,
      variationId,
      termosAceitos,
      chatMensagens,
      chatInput,
      lexiDigitando,
      invoiceUrl,
      iniciandoPagamento,
      pagamentoError,
      pollingPagamentoExpirado,
      emailValido,
      showNomeError,
      showEmailError,
      showCnpjError,
      showRazaoSocialError,
      showNomeFantasiaError,
      showSiteError,
      podeBuscar,
      podeAvancarPerfil,
      podeAvancarParametrosStep,
      wizardSteps,
      objetivoLabel,
      stepAnteriorRevisao,
      planoResumoLabel,
      goTo,
      avancarDoPerfil,
      selecionarObjetivo,
      atualizarPerfil,
      consultarCnpjEmpresa,
      reconsultarCnpjEmpresa,
      buscarContexto,
      editarDados,
      criarAgente,
      cancelarCriacaoAgente,
      definirValorParametro,
      recarregarObjetivos,
      recarregarCampos,
      tentarBuscaNovamente,
      recarregarPlanos,
      definirPlano,
      alternarTermos,
      seguirPagamento,
      tentarPagamentoNovamente,
      verificarPagamentoAgora,
      definirChatInput,
      enviarMensagem,
      reiniciarFluxo
    };
    $[134] = alternarTermos;
    $[135] = atualizarPerfil;
    $[136] = avancarDoPerfil;
    $[137] = buscandoContexto;
    $[138] = buscarContexto;
    $[139] = buscouContexto;
    $[140] = camposError;
    $[141] = camposObjetivo;
    $[142] = carregandoCampos;
    $[143] = carregandoObjetivos;
    $[144] = carregandoPlanos;
    $[145] = chatInput;
    $[146] = chatMensagens;
    $[147] = cnpjEncontrado;
    $[148] = cnpjError;
    $[149] = consultandoCnpj;
    $[150] = consultarCnpjEmpresa;
    $[151] = contextoEncontrado;
    $[152] = contextoError;
    $[153] = criandoAgente;
    $[154] = criarAgente;
    $[155] = criarAgenteError;
    $[156] = definirChatInput;
    $[157] = definirPlano;
    $[158] = definirValorParametro;
    $[159] = editarDados;
    $[160] = emailValido;
    $[161] = enviarMensagem;
    $[162] = goTo;
    $[163] = iniciandoPagamento;
    $[164] = invoiceUrl;
    $[165] = lexiDigitando;
    $[166] = objetivo;
    $[167] = objetivoLabel;
    $[168] = objetivosCatalogo;
    $[169] = objetivosError;
    $[170] = pagamentoError;
    $[171] = perfil;
    $[172] = planoResumoLabel;
    $[173] = planosCards;
    $[174] = planosError;
    $[175] = podeAvancarParametrosStep;
    $[176] = podeAvancarPerfil;
    $[177] = podeBuscar;
    $[178] = pollingPagamentoExpirado;
    $[179] = recarregarCampos;
    $[180] = recarregarObjetivos;
    $[181] = recarregarPlanos;
    $[182] = reconsultarCnpjEmpresa;
    $[183] = reiniciarFluxo;
    $[184] = seguirPagamento;
    $[185] = selecionarObjetivo;
    $[186] = showCnpjError;
    $[187] = showEmailError;
    $[188] = showNomeError;
    $[189] = showNomeFantasiaError;
    $[190] = showRazaoSocialError;
    $[191] = showSiteError;
    $[192] = step;
    $[193] = stepAnteriorRevisao;
    $[194] = tentarBuscaNovamente;
    $[195] = tentarPagamentoNovamente;
    $[196] = termosAceitos;
    $[197] = valoresParametros;
    $[198] = variationId;
    $[199] = verificarPagamentoAgora;
    $[200] = wizardSteps;
    $[201] = t50;
  } else {
    t50 = $[201];
  }
  return t50;
};
export type UseNegociacaoSelfReturn = ReturnType<typeof useNegociacaoSelf>;